import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingProvider } from '@app/components/_shared/loading-spinner/loading-spinner.component';
import { ModalProvider } from '@app/components/_shared/modal/modal.component';
import { Breadcrumb } from '@app/models/_base/breadcrumb.model';
import { CourseFilter } from '@app/models/courses/filter.model';
import { PrimaryCourseFiltersRequest } from '@app/models/courses/filterRequest.primary.model';
import { CourseFiltersResponse } from '@app/models/courses/filterResponse.model';
import { CourseService } from '@app/services/course.service';
import { Footer } from '@app/services/footer.service';
import { LocalisationService } from '@app/services/localisation.service';
import { NavigationService } from '@app/services/navigation.service';
import { QuestionnaireService } from '@app/services/questionnaire.service';
import { RouteBreadcrumbs } from '@app/services/routeBreadcrumbs.service';
import { StringFormatPipe } from '@appShared/pipes/string-format.pipe';
import * as _ from 'lodash';

@Component({
  selector: 'app-course-area-apply-filters',
  templateUrl: './course-area-apply-filters.component.html',
  styleUrls: ['./course-area-apply-filters.component.scss'],
  providers: [StringFormatPipe]
})
export class CourseAreaApplyFiltersComponent implements OnInit {
  public static route: string = 'course-area-filters';

  locale: any = {};

  filters: CourseFilter[] = [];
  filtersLoaded: boolean = false;
  coursesAvailable: number = 0;

  title: string = '';
  subtitle: string = '';

  //Grade points
  gradeCourseFilter: CourseFilter = new CourseFilter();

  //Budget Filter Attributes
  budgetCourseFilter: CourseFilter = new CourseFilter();

  //Language Filter Attributes
  languageCourseFilter: CourseFilter = new CourseFilter();

  //Location Filter Attributes
  locationCourseFilter: CourseFilter = new CourseFilter();

  //Course Filter Attributes
  courseCourseFilter: CourseFilter = new CourseFilter();

  //Joint Course Attributes
  showJointCourse: boolean = false;
  jointCourseFilter: CourseFilter = new CourseFilter();

  //Apprenticehips Course Attributes
  showApprCourse: boolean = false;
  apprCourseFilter: CourseFilter = new CourseFilter();

  incomplete: boolean = false;

  constructor(
    private router: Router,
    private routeBreadcrumbs: RouteBreadcrumbs,
    private footerService: Footer,
    private courseService: CourseService,
    private questionnaireService: QuestionnaireService,
    private spinner: LoadingProvider,
    private localisation: LocalisationService,
    private modal: ModalProvider,
    private navigationService: NavigationService,
    private stringPipe: StringFormatPipe
  ) {}

  ngOnInit() {
    this.locale = this.localisation.getTranslationsForComponent('CourseAreaApplyFiltersComponent');

    this.routeBreadcrumbs.breadcrumbs.next([new Breadcrumb(this.locale.breadcrumbText, '')]);

    this.getPrimaryCourseFilters(null);

    if (sessionStorage.getItem('filter-incomplete') == '1') {
      this.incomplete = true;
      sessionStorage.removeItem('filter-incomplete');
    }
  }

  getPrimaryCourseFilters(filters: PrimaryCourseFiltersRequest) {
    this.spinner.show(this.locale.loadingText, true);
    this.filtersLoaded = false;

    this.courseService
      .getPrimaryCourseFilters(filters)
      .finally(() => {
        this.spinner.hide();
      })
      .subscribe(filters => {
        this.navigationService.updateFiltersComplete(
          !filters.filters.filter(e => e.isRequired).some(e => !e.completed)
        );
        if (filters.filters.filter(e => e.isRequired).some(e => !e.completed)) {
          this.questionnaireService.statusOverride.next(300);
        } else {
          this.questionnaireService.statusOverride.next(null);
        }
        this.updateView(filters);
        this.filtersLoaded = true;
      });
  }

  updateJointCourses() {
    /*
    * Now Reset any settings prior
    */
    this.courseService.updateCategorySelected(2);
    this.courseService.deleteSecondaryFilters().subscribe(filters => {});

    this.showJointCourse = !this.showJointCourse;
    this.jointCourseFilter.completed = true;

    _.forEach(this.jointCourseFilter.options, (opt, key) => {
      opt.selected = !opt.selected;
    });

    this.getPrimaryCourseFilters(new PrimaryCourseFiltersRequest(this.filters));
  }

  updateApprCourses() {
    /*
    * Now Reset any settings prior
    */
    this.courseService.updateCategorySelected(2);
    this.courseService.deleteSecondaryFilters().subscribe(filters => {});

    this.showApprCourse = !this.showApprCourse;
    this.apprCourseFilter.completed = true;

    _.forEach(this.apprCourseFilter.options, (opt, key) => {
      opt.selected = !opt.selected;
    });

    this.getPrimaryCourseFilters(new PrimaryCourseFiltersRequest(this.filters));
  }

  resetFilters() {
    this.filters.forEach(filter => (filter.completed = false));
    this.courseService.deletePrimaryCourseFilters().subscribe(filters => {
      this.updateView(filters);
    });
  }

  applyFilters() {
    if (this.isValid()) this.router.navigateByUrl('centigrade/courses/list');
    else
      this.modal.create(
        this.locale.invalidFiltersModalTitle,
        this.locale.invalidFiltersModalMessage
      );
  }

  private updateView(filters: CourseFiltersResponse) {
    this.coursesAvailable = filters.count;

    let footerText = this.stringPipe.transform(this.locale.footerText, [
      this.coursesAvailable.toString()
    ]);

    this.footerService.update({
      title: footerText,
      action: () => this.applyFilters()
    });

    this.filters = filters.filters;
    this.title = filters.pageTitle;
    this.subtitle = filters.pageText;

    //Populate join course filter toggle area.
    this.jointCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_JOINT_COURSES');
    this.apprCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_APPR');
    this.gradeCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_TARIFF');
    this.budgetCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_BUDGET');
    this.languageCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_LANGUAGES');
    this.locationCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_LOCATION');
    this.courseCourseFilter = this.filters.find(filter => filter.tag == 'FTYPE_COURSE');

    if (this.jointCourseFilter && this.jointCourseFilter.completed) {
      let option = this.jointCourseFilter.options.find(opt => opt.id == 1);
      this.showJointCourse = option.selected;
    }
    if (this.apprCourseFilter && this.apprCourseFilter.completed) {
      let option = this.apprCourseFilter.options.find(opt => opt.id == 1);
      this.showApprCourse = option.selected;
    }
  }

  private isValid(): boolean {
    return !this.filters.some(f => f.isRequired && !f.completed);
  }

  private goToFilter(tag: string) {
    switch (tag) {
      case 'FTYPE_TARIFF': {
        this.router.navigate(['/centigrade/course-area-filters/grades-filter']);
        break;
      }
      case 'FTYPE_BUDGET': {
        this.router.navigate(['/centigrade/course-area-filters/course-fees-filter']);
        break;
      }
      case 'FTYPE_LANGUAGES': {
        this.router.navigate(['/centigrade/course-area-filters/language-filter']);
        break;
      }
      case 'FTYPE_LOCATION': {
        this.router.navigate(['/centigrade/course-area-filters/location-filter']);
        break;
      }
      case 'FTYPE_COURSE': {
        this.router.navigate(['/centigrade/course-area-filters/courses-filter']);
        break;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/course-areas/filters/course-area-apply-filters/course-area-apply-filters.component.ts